import {
  makeRedeemCode,
  pickWeighted,
} from "@/components/marketing";
import { useEffect, useRef, useState } from "react";
import type { ScratchJourney, ScratchJourneyKey } from "../../../data";

type ScratchPhase = "card" | "offer" | "done";

interface UseScratchCardInput {
  journeys: ScratchJourney[];
}

// Scratch & Win state: pick a journey (before or after payment), scratch or
// reveal the card, then redeem. Every new card (or journey switch) draws a
// weighted prize and bumps `cardId` so the foil is repainted. The first card
// shows the journey's first prize so the server and client render the same.
export function useScratchCard({ journeys }: UseScratchCardInput) {
  const initialState = {
    journeyKey: journeys[0].key as ScratchJourneyKey,
    prize: journeys[0].prizes[0].label,
    phase: "card" as ScratchPhase,
    code: "",
    cardId: 0,
  };
  const [state, setState] = useState(initialState);
  const hasInteractedRef = useRef(false);
  const redeemButtonRef = useRef<HTMLButtonElement>(null);
  const newCardButtonRef = useRef<HTMLButtonElement>(null);
  const journey =
    journeys.find((item) => item.key === state.journeyKey) ?? journeys[0];

  // Revealing shows the redeem button and redeeming removes it, so move
  // focus along (only after the visitor has used the demo).
  useEffect(() => {
    function moveFocus() {
      if (!hasInteractedRef.current) return;
      if (state.phase === "offer")
        redeemButtonRef.current?.focus({ preventScroll: true });
      if (state.phase === "done") newCardButtonRef.current?.focus();
    }
    moveFocus();
  }, [state.phase]);

  const dealCard = (journeyKey: ScratchJourneyKey) => {
    const next =
      journeys.find((item) => item.key === journeyKey) ?? journeys[0];
    const prize = next.prizes[pickWeighted(next.prizes)].label;
    setState((state) => ({
      ...state,
      journeyKey,
      prize,
      phase: "card",
      code: "",
      cardId: state.cardId + 1,
    }));
  };

  const selectJourney = (journeyKey: ScratchJourneyKey) => dealCard(journeyKey);
  const newCard = () => dealCard(state.journeyKey);

  const reveal = () => {
    hasInteractedRef.current = true;
    setState((state) =>
      state.phase === "card" ? { ...state, phase: "offer" } : state,
    );
  };

  const redeem = () =>
    setState((state) => ({ ...state, phase: "done", code: makeRedeemCode() }));

  return {
    journey,
    prize: state.prize,
    phase: state.phase,
    isOpen: state.phase !== "card",
    code: state.code,
    cardId: state.cardId,
    selectJourney,
    newCard,
    reveal,
    redeem,
    redeemButtonRef,
    newCardButtonRef,
  };
}
